import { z } from 'zod';
import { HexColor } from './colors';
import { ApiKey, Uuid } from './ids';
import { DescriptionText, LabelText } from './metamodel';
import { type LocalizedText, localizedText, resolveText } from './localized-text';

// ---------------------------------------------------------------------------------------------
// Option sets: reusable option lists for select and multi-select fields
// ---------------------------------------------------------------------------------------------

/**
 * A workspace-wide option list ("Industries", "Countries", "Priority"). A select or multi-select
 * field either keeps its own `config.options` or names a set in `config.optionSet`; the model then
 * carries the set's options in the field's `config.options`, so every reader (validation, sorting,
 * filters, pills, pickers) works unchanged. Changing a set changes every field that uses it.
 */
export const OPTION_SET_CONFIG_KEY = 'optionSet';
/** The field types whose options can come from an option set. */
export const OPTION_SET_FIELD_TYPES = ['select', 'multi_select'] as const;

export const OptionKey = z
  .string()
  .min(1)
  .max(80)
  .regex(/^[a-z0-9][a-z0-9_-]*$/, { error: 'Use lower-case letters, digits, "_" or "-"' });
export const OptionLabelText = localizedText({ minLength: 1, maxLength: 120 });
export const DEFAULT_OPTION_COLOR = '#98A2B3';

/** One option as written: the label is plain or one value per locale. */
export const RawOptionSetOption = z.object({
  key: OptionKey,
  label: OptionLabelText,
  color: HexColor.default(DEFAULT_OPTION_COLOR),
});
export type RawOptionSetOption = z.infer<typeof RawOptionSetOption>;
export const OptionSetOption = RawOptionSetOption.extend({ label: z.string() });
export type OptionSetOption = z.infer<typeof OptionSetOption>;

/** A field whose options come from the set. */
export const OptionSetUsage = z.object({
  fieldId: Uuid,
  fieldKey: ApiKey,
  fieldLabel: z.string(),
  objectKey: ApiKey,
  objectLabel: z.string(),
});
export type OptionSetUsage = z.infer<typeof OptionSetUsage>;

export const OptionSet = z.object({
  id: Uuid,
  key: ApiKey,
  label: z.string(),
  description: z.string().nullable(),
  options: z.array(OptionSetOption),
  usage: z.array(OptionSetUsage),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OptionSet = z.infer<typeof OptionSet>;

/** An option set with its label, description and option labels as written (`?raw=1`). */
export const RawOptionSet = OptionSet.extend({
  label: LabelText,
  description: DescriptionText.nullable(),
  options: z.array(RawOptionSetOption),
});
export type RawOptionSet = z.infer<typeof RawOptionSet>;

const uniqueKeys = (options: readonly { key: string }[]) =>
  new Set(options.map((o) => o.key)).size === options.length;
const Options = z
  .array(RawOptionSetOption)
  .max(1000)
  .refine(uniqueKeys, { message: 'Option keys must be unique' });

export const CreateOptionSetInput = z.object({
  key: ApiKey,
  label: LabelText,
  description: DescriptionText.nullish(),
  options: Options.default([]),
});
export type CreateOptionSetInput = z.input<typeof CreateOptionSetInput>;

/** `options` replaces the whole list; a plain label of a translated option sets the writer's language. */
export const UpdateOptionSetInput = z
  .object({ label: LabelText, description: DescriptionText.nullable(), options: Options })
  .partial();
export type UpdateOptionSetInput = z.infer<typeof UpdateOptionSetInput>;

/** The set a field config names, or null (own options). */
export function optionSetKeyOf(config: Readonly<Record<string, unknown>> | null | undefined): string | null {
  const key = config?.[OPTION_SET_CONFIG_KEY];
  return typeof key === 'string' && key.length > 0 ? key : null;
}

/** Whether a field type can take its options from a set. */
export function acceptsOptionSet(type: string): boolean {
  return (OPTION_SET_FIELD_TYPES as readonly string[]).includes(type);
}

export function resolveOptionSet(set: RawOptionSet, locale: string): OptionSet {
  const text = (t: LocalizedText) => resolveText(t, locale);
  return {
    ...set,
    label: text(set.label),
    description: set.description == null ? null : text(set.description),
    options: set.options.map((o) => ({ ...o, label: text(o.label) })),
  };
}
